import { Component, ChangeDetectionStrategy, inject, computed, signal, effect } from "@angular/core";
import { UpperCasePipe } from "@angular/common";
import { ReactiveFormsModule, FormGroup, FormControl, Validators } from "@angular/forms";
import { toSignal } from "@angular/core/rxjs-interop";
import { Store } from "@ngrx/store";
import { TranslateModule } from "@ngx-translate/core";

import { DynamicDialogRef } from "primeng/dynamicdialog";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { SelectModule } from "primeng/select";
import { FloatLabelModule } from "primeng/floatlabel";

import { CreateVersion } from "src/api-service";
import { VersionNameValidator } from "src/app/shared/validators/validations";
import { selectCurrencies } from "src/app/store/application/data/currency/currencies/currencies.selectors";
import { selectAvailableLangs } from "src/app/store/application/settings/settings.selectors";
import { selectQuoteState } from "src/app/store/pages/quote/quote.selectors";
import { selectVersion } from "src/app/store/pages/quote/version/version.selectors";
import { QuoteVersionActions } from "src/app/store/pages/quote/version/version.actions";
import { getLanguageFlagUrl, getCurrencyFlagUrl } from "src/app/shared/constants/flags.constants";

@Component({
    selector: "app-create-version-dialog",
    templateUrl: "./create-version-dialog.component.html",
    styleUrls: ["./create-version-dialog.component.scss"],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        UpperCasePipe,
        ReactiveFormsModule,
        TranslateModule,
        ButtonModule,
        InputTextModule,
        IconFieldModule,
        InputIconModule,
        SelectModule,
        FloatLabelModule,
    ],
})
export class CreateVersionDialogComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    public readonly ref = inject(DynamicDialogRef);
    private readonly store = inject(Store);

    // =========================================================================
    // CONSTANTS
    // =========================================================================
    public readonly NAME_MAX_LENGTH = 50;

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    public readonly quote = toSignal(this.store.select(selectQuoteState));
    public readonly currencies = toSignal(this.store.select(selectCurrencies), { initialValue: [] });
    private readonly selectedVersion = toSignal(this.store.select(selectVersion));

    // =========================================================================
    // STORE SIGNALS - Application settings
    // =========================================================================
    public readonly availableLangs = toSignal(this.store.select(selectAvailableLangs), { initialValue: [] });

    private readonly sourceData = computed(() => {
        const version = this.selectedVersion();
        const currencies = this.currencies();
        return {
            sourceVersionId: version?.id,
            sourceVersionLanguage: version?.languageCode?.toLowerCase() ?? "sv",
            sourceVersionCurrencyCode: version?.currencyCode ?? "SEK",
            sourceVersionExchangeRate: version?.currencyExchangeRateSnapshot ?? 1.0,
            sourceVersionSnapshotDate: version?.currencySnapshotDate ?? currencies.find(x => x.code === "SEK")?.updated,
        };
    });

    // =========================================================================
    // FORM STATE
    // =========================================================================
    public readonly form = new FormGroup({
        name: new FormControl("", [
            Validators.required,
            Validators.maxLength(this.NAME_MAX_LENGTH),
            VersionNameValidator(),
        ]),
        versionLanguage: new FormControl("sv", Validators.required),
        versionCurrency: new FormControl("SEK", Validators.required),
    });

    private readonly formInitialized = signal(false);

    // =========================================================================
    // EFFECTS
    // =========================================================================
    private readonly initFormEffect = effect(() => {
        const source = this.sourceData();
        if (source.sourceVersionId && !this.formInitialized()) {
            this.formInitialized.set(true);
            this.form.patchValue({
                versionLanguage: source.sourceVersionLanguage,
                versionCurrency: source.sourceVersionCurrencyCode,
            });
        }
    });

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    public displayCurrencyChangeWarning(): boolean {
        return this.form.value.versionCurrency !== this.sourceData().sourceVersionCurrencyCode;
    }

    public currencyCodeWasChanged(): boolean {
        return this.form.value.versionCurrency !== this.sourceData().sourceVersionCurrencyCode;
    }

    public createVersion(): void {
        const source = this.sourceData();
        const currencies = this.currencies();
        const selectedCurrency = currencies.find(x => x.code === this.form.value.versionCurrency);

        const request: CreateVersion = {
            name: this.form.value.name ?? "",
            languageCode: this.form.value.versionLanguage ?? "sv",
            currencyCode: this.form.value.versionCurrency ?? "SEK",
            currencyExchangeRate: this.currencyCodeWasChanged()
                ? selectedCurrency?.purchaseExchangeRate
                : source.sourceVersionExchangeRate,
            currencySnapshotDate: this.currencyCodeWasChanged()
                ? selectedCurrency?.updated
                : source.sourceVersionSnapshotDate,
        };

        if (source.sourceVersionId) {
            this.store.dispatch(
                QuoteVersionActions.createNewVersion({
                    versionId: source.sourceVersionId,
                    request,
                }),
            );
        }
        this.ref.close();
    }

    public closeDialog(): void {
        this.ref.close();
    }

    public getLanguageFlagUrl(langCode: string): string {
        return getLanguageFlagUrl(langCode);
    }

    public getCurrencyFlagUrl(currencyCode: string): string {
        return getCurrencyFlagUrl(currencyCode);
    }
}
